import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Check, Type, Pen } from 'lucide-react';

const TelestrationCanvas = ({
    width,
    height,
    isDrawing,
    initialData,
    onSave,
    onCancel,
    readOnly = false,
    authorName
}) => {
    const canvasRef = useRef(null);
    const [isPainting, setIsPainting] = useState(false);
    const [tool, setTool] = useState('pen'); // 'pen' | 'text'
    const [textInput, setTextInput] = useState({ visible: false, x: 0, y: 0, value: '' });

    // Initialize canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');

        // Set size
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        // Default style
        updateContextStyle(ctx);

        // Load initial data if exists
        if (initialData && initialData.image) {
            const img = new Image();
            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            };
            img.src = initialData.image;
        }
    }, [initialData]);

    const updateContextStyle = (ctx) => {
        ctx.strokeStyle = '#facc15'; // Yellow
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    };

    const getCoordinates = (event) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            x: (event.clientX - rect.left) * scaleX,
            y: (event.clientY - rect.top) * scaleY
        };
    };

    const startPaint = (event) => {
        if (readOnly) return;
        const coordinates = getCoordinates(event);
        if (!coordinates) return;

        if (tool === 'text') {
            // If text input is already open, don't open another one, user should finish first
            if (textInput.visible) return;

            setTextInput({
                visible: true,
                x: coordinates.x,
                y: coordinates.y,
                value: ''
            });
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        updateContextStyle(ctx);

        setIsPainting(true);
        ctx.beginPath();
        ctx.moveTo(coordinates.x, coordinates.y);
    };

    const paint = (event) => {
        if (!isPainting || readOnly || tool !== 'pen') return;

        const coordinates = getCoordinates(event);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        ctx.lineTo(coordinates.x, coordinates.y);
        ctx.stroke();
    };

    const exitPaint = () => {
        setIsPainting(false);
    };

    const handleTextSubmit = () => {
        if (!textInput.value.trim()) {
            setTextInput(prev => ({ ...prev, visible: false }));
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        drawBubble(ctx, textInput.x, textInput.y, textInput.value);

        setTextInput(prev => ({ ...prev, visible: false, value: '' }));
        // Switch back to pen after adding text? Or stay in text mode?
        // Let's stay in text mode for multiple notes
    };

    const drawBubble = (ctx, x, y, text) => {
        ctx.font = '16px sans-serif';
        const padding = 12;
        const lineHeight = 20;
        const maxWidth = 200;

        // Wrap text
        const words = text.split(' ');
        let line = '';
        const lines = [];

        for (let n = 0; n < words.length; n++) {
            const testLine = line + words[n] + ' ';
            const metrics = ctx.measureText(testLine);
            const testWidth = metrics.width;
            if (testWidth > maxWidth && n > 0) {
                lines.push(line);
                line = words[n] + ' ';
            } else {
                line = testLine;
            }
        }
        lines.push(line);

        const bubbleWidth = maxWidth + (padding * 2);
        const bubbleHeight = (lines.length * lineHeight) + (padding * 2);

        // Draw rounded box (Snapp Theme: Dark bg, Blue/Gray border)
        const r = 8; // rounded radius

        // Position: centered on x, above y? Or just at x,y?
        // Let's place top-left at x,y for simplicity or centered if preferred.
        // User clicks -> box appears. Usually box top-left at click is easiest predict.
        const bx = x;
        const by = y;

        ctx.beginPath();
        ctx.moveTo(bx + r, by);
        ctx.lineTo(bx + bubbleWidth - r, by);
        ctx.quadraticCurveTo(bx + bubbleWidth, by, bx + bubbleWidth, by + r);
        ctx.lineTo(bx + bubbleWidth, by + bubbleHeight - r);
        ctx.quadraticCurveTo(bx + bubbleWidth, by + bubbleHeight, bx + bubbleWidth - r, by + bubbleHeight);
        ctx.lineTo(bx + r, by + bubbleHeight);
        ctx.quadraticCurveTo(bx, by + bubbleHeight, bx, by + bubbleHeight - r);
        ctx.lineTo(bx, by + r);
        ctx.quadraticCurveTo(bx, by, bx + r, by);
        ctx.closePath();

        // Dark theme background
        ctx.fillStyle = '#1a1a1b';
        ctx.fill();

        // Border
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#3b82f6'; // Blue-500
        ctx.stroke();

        // Draw text (White)
        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'top';
        lines.forEach((l, i) => {
            ctx.fillText(l.trim(), bx + padding, by + padding + (i * lineHeight));
        });
    };

    const handleSave = () => {
        const canvas = canvasRef.current;
        // Save as Data URL
        const dataUrl = canvas.toDataURL('image/png');
        onSave && onSave({
            image: dataUrl,
            text: textInput.value ? textInput.value : "Drawing" // Simple metadata fallback
        });
    };

    return (
        <div className="absolute inset-0 z-10 pointer-events-none">
            <div className={`relative w-full h-full ${readOnly ? 'pointer-events-none' : 'pointer-events-auto'}`}>
                <canvas
                    ref={canvasRef}
                    className={`w-full h-full touch-none ${tool === 'text' ? 'cursor-text' : 'cursor-crosshair'}`}
                    onMouseDown={startPaint}
                    onMouseMove={paint}
                    onMouseUp={exitPaint}
                    onMouseLeave={exitPaint}
                />

                {/* Text Input Overlay */}
                {textInput.visible && (
                    <div
                        className="absolute pointer-events-auto bg-[#1a1a1b] p-3 rounded-lg shadow-xl border border-blue-600/50"
                        style={{
                            left: textInput.x,
                            top: textInput.y,
                        }}
                    >
                        <textarea
                            autoFocus
                            className="bg-transparent border-none outline-none text-white text-sm resize-none min-w-[200px]"
                            rows={3}
                            placeholder="Notunuzu yazın..."
                            value={textInput.value}
                            onChange={(e) => setTextInput(prev => ({ ...prev, value: e.target.value }))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleTextSubmit();
                                }
                                if (e.key === 'Escape') {
                                    setTextInput(prev => ({ ...prev, visible: false }));
                                }
                            }}
                        />
                        <div className="flex justify-end mt-2 gap-2">
                            <Button size="xs" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-white" onClick={() => setTextInput(prev => ({ ...prev, visible: false }))}>
                                İptal
                            </Button>
                            <Button size="xs" className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleTextSubmit}>
                                Ekle
                            </Button>
                        </div>
                    </div>
                )}

                {/* Author Label */}
                {readOnly && authorName && (
                    <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-md text-sm font-medium border border-white/20">
                        Note by {authorName}
                    </div>
                )}

                {/* Edit Controls & Tools - Split Layout */}
                {!readOnly && (
                    <>
                        {/* Tool Switcher - Bottom Center */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-auto">
                            <div className="flex gap-1 bg-[#1a1a1b]/90 p-1 rounded-lg border border-gray-700 shadow-lg backdrop-blur-sm">
                                <button
                                    onClick={() => setTool('pen')}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${tool === 'pen' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                                >
                                    <Pen className="w-4 h-4" />
                                    <span className="font-medium">Çiz</span>
                                </button>
                                <button
                                    onClick={() => setTool('text')}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${tool === 'text' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                                >
                                    <Type className="w-4 h-4" />
                                    <span className="font-medium">Yaz</span>
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons - Bottom Right */}
                        <div className="absolute bottom-4 right-4 pointer-events-auto">
                            <div className="flex gap-2">
                                <Button size="sm" variant="destructive" onClick={onCancel} className="shadow-lg">
                                    <X className="w-4 h-4 mr-1" />
                                    İptal
                                </Button>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 shadow-lg" onClick={handleSave}>
                                    <Check className="w-4 h-4 mr-1" />
                                    Kaydet
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TelestrationCanvas;
